import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHeader, TableRow, TableHead } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider
} from '../../components/ui/tooltip';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { useToast } from '../../components/ui/use-toast';
import { Loader } from '../../components/ui/loader';

// icons
import { Add, Edit, Trash, Shield, UserSquare, Key, SearchNormal } from 'iconsax-react';

// services
import UserService from '../../services/userService';
import roleService from '../../services/roleService';

// utils
import { getPermissionErrorMessage } from '../../utils/permissionMessages';

// Memoized search input component
const SearchInput = memo(({ value, onChange, inputRef, onSelectionChange }) => {
  return (
    <div className="relative w-full">
      <SearchNormal
        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        size={18}
      />
      <Input
        ref={inputRef}
        placeholder="Search users..."
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          onSelectionChange(e.target.selectionStart);
        }}
        onSelect={(e) => onSelectionChange(e.target.selectionStart)}
        className="pl-9 w-full bg-background"
      />
    </div>
  );
});

// Validation schema
const userValidationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must not exceed 100 characters'),
  email: Yup.string()
    .required('Email is required')
    .email('Invalid email format')
    .max(255, 'Email must not exceed 255 characters'),
  role: Yup.string()
    .required('Role is required'),
  status: Yup.string()
    .oneOf(['ACTIVE', 'INACTIVE'], 'Invalid status')
});

const UserManagement = () => {
  const { toast } = useToast();
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ totalItems: 0, totalPages: 0, currentPage: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [globalStats, setGlobalStats] = useState({ active: 0, inactive: 0, superAdmins: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const searchInputRef = useRef(null);
  const cursorPositionRef = useRef(0);

  // Load available roles on component mount
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const result = await roleService.getAllRoles({ roleType: 'ADMIN', limit: 100 });
        console.log('Fetched roles:', result);
        
        if (result.success) {
          // Handle nested data structure from backend
          const rolesData = result.data.roles || result.data || [];
          // Map all returned admin roles (both system and custom platform roles)
          const roleOptions = rolesData.map(role => ({
            value: role.name,
            label: role.displayName || role.name
          }));
          setAvailableRoles(roleOptions);
        }

        // Fetch global role stats
        try {
          const statsResult = await UserService.getRoleStats();
          if (statsResult.success && statsResult.data?.adminUsers) {
             const superAdmins = statsResult.data.adminUsers.find(r => r._id === 'SUPER_ADMIN')?.count || 0;
             setGlobalStats(prev => ({ ...prev, superAdmins }));
          }
        } catch (e) {
          console.error('Failed to load role stats:', e);
        }
      } catch (error) {
        console.error('Error loading roles:', error);
        
        // Check if it's a permission error
        if (error.response?.data?.error?.code === 'INSUFFICIENT_PERMISSIONS') {
          const friendlyMessage = getPermissionErrorMessage(error.response.data.error.message);
          setError(friendlyMessage);
        } else {
          // Fallback to hardcoded roles if API fails
          setAvailableRoles([
            { value: 'SUPER_ADMIN', label: 'Super Administrator' },
            { value: 'PLATFORM_ADMIN', label: 'Platform Administrator' },
            { value: 'SUPPORT_ADMIN', label: 'Support Administrator' },
            { value: 'BILLING_ADMIN', label: 'Billing Administrator' }
          ]);
        }
      }
    };
    loadRoles();
  }, []);

  const getInitialFormValues = () => ({
    name: selectedUser?.name || '',
    email: selectedUser?.email || '',
    role: selectedUser?.role || '',
    status: selectedUser?.status || 'ACTIVE',
    sendInvitation: true
  });

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        page: page + 1, // Backend expects 1-based page
        limit: rowsPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter && { role: roleFilter }),
        ...(statusFilter && { status: statusFilter })
      };

      const result = await UserService.getAllUsers(params);
      console.log('Fetched users:', result);
      
      if (result.success) {
        setUsers(result.data.users || []);
        setPagination(result.data.pagination || { totalItems: 0, totalPages: 0, currentPage: 1 });
        if (result.data.statusCounts) {
           setGlobalStats(prev => ({
              ...prev,
              active: result.data.statusCounts.active || 0,
              inactive: result.data.statusCounts.inactive || 0
           }));
        }
        setError('');
      } else {
        if (result.error?.code === 'INSUFFICIENT_PERMISSIONS') {
          const friendlyMessage = getPermissionErrorMessage(result.error.message);
          setError(friendlyMessage);
        } else {
          setError(result.message);
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      
      // Check if it's a permission error
      if (error.response?.data?.error?.code === 'INSUFFICIENT_PERMISSIONS') {
        const friendlyMessage = getPermissionErrorMessage(error.response.data.error.message);
        setError(friendlyMessage);
      } else {
        setError('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, roleFilter, statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Restore cursor position after re-render
  useEffect(() => {
    if (searchInputRef.current) {
      const input = searchInputRef.current;
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
      }, 0);
    }
  }, [users]);

  const handleCreateUser = () => {
    setDialogType('create');
    setSelectedUser(null);
    setSubmitError('');
    setSubmitSuccess('');
    setOpenDialog(true);
  };

  const handleEditUser = (user) => {
    setDialogType('edit');
    setSelectedUser(user);
    setSubmitError('');
    setSubmitSuccess('');
    setOpenDialog(true);
  };

  const handleDeleteUser = (user) => {
    setDialogType('delete');
    setSelectedUser(user);
    setSubmitError('');
    setSubmitSuccess('');
    setOpenDialog(true);
  };

  const handleResetPassword = (user) => {
    setDialogType('resetPassword');
    setSelectedUser(user);
    setSubmitError('');
    setSubmitSuccess('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setSubmitError('');
    setSubmitSuccess('');
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      setSubmitError('');
      setSubmitSuccess('');
      
      if (dialogType === 'create') {
        const result = await UserService.createUser(values);
        if (result.success) {
          toast({
            title: 'Success',
            description: 'User created successfully!'
          });
          await fetchUsers();
          handleCloseDialog();
        } else {
          setSubmitError(result.message || 'Failed to create user');
        }
      } else if (dialogType === 'edit') {
        const result = await UserService.updateUser(selectedUser._id, {
          name: values.name,
          email: values.email,
          role: values.role,
          status: values.status
        });
        if (result.success) {
          toast({
            title: 'Success',
            description: 'User updated successfully!'
          });
          await fetchUsers();
          handleCloseDialog();
        } else {
          setSubmitError(result.message || 'Failed to update user');
        }
      }
    } catch (error) {
      console.error(`Error ${dialogType} user:`, error);
      setSubmitError(error.response?.data?.message || `Failed to ${dialogType} user. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setSubmitError('');
      
      const result = await UserService.deleteUser(selectedUser._id);
      if (result.success) {
        toast({
          title: 'Success',
          description: 'User deleted successfully!'
        });
        await fetchUsers();
        handleCloseDialog();
      } else {
        setSubmitError(result.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setSubmitError(error.response?.data?.message || 'Failed to delete user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      setLoading(true);
      setSubmitError('');
      
      const result = await UserService.resetUserPassword(selectedUser._id, true);
      if (result.success) {
        toast({
          title: 'Success',
          description: 'Password reset successfully!'
        });
        handleCloseDialog();
      } else {
        setSubmitError(result.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setSubmitError(error.response?.data?.message || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (user) => {
    try {
      const newStatus = user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      const result = await UserService.updateUserStatus(user._id, newStatus);
      if (result.success) {
        toast({
          title: 'Success',
          description: `User status updated to ${newStatus.toLowerCase()} successfully.`
        });
        await fetchUsers();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Failed to update user status');
    }
  };

  const handleSearchChange = (value) => {
    setInputValue(value);
    setPage(0); // Reset to first page when searching
  };

  const handleRoleFilterChange = (value) => {
    setRoleFilter(value === 'all' ? '' : value);
    setPage(0); // Reset to first page when filtering
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value === 'all' ? '' : value);
    setPage(0); // Reset to first page when filtering
  };

  const getUserStats = () => {
    const totalUsers = pagination.totalItems || 0;
    const activeUsers = globalStats.active || 0;
    const inactiveUsers = globalStats.inactive || 0;
    const superAdmins = globalStats.superAdmins || 0;

    return { totalUsers, activeUsers, inactiveUsers, superAdmins };
  };

  const stats = getUserStats();



  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          User Management
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage administrative user accounts and permissions
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-primary">
                  {loading ? '...' : stats.totalUsers}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Total Users
                </p>
              </div>
              <div className="p-2 bg-primary/10 rounded-lg">
                <UserSquare size={24} className="text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-emerald-600">
                  {loading ? '...' : stats.activeUsers}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Active Users
                </p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Shield size={24} className="text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-amber-600">
                  {loading ? '...' : stats.inactiveUsers}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Inactive Users
                </p>
              </div>
              <div className="p-2 bg-amber-100 rounded-lg">
                <Shield size={24} className="text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow duration-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-3xl font-bold text-sky-600">
                  {loading ? '...' : stats.superAdmins}
                </p>
                <p className="text-sm font-medium text-muted-foreground mt-1">
                  Super Admins
                </p>
              </div>
              <div className="p-2 bg-sky-100 rounded-lg">
                <Shield size={24} className="text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="w-full md:w-1/3">
              <SearchInput 
                value={inputValue}
                onChange={handleSearchChange}
                inputRef={searchInputRef}
                onSelectionChange={(pos) => { cursorPositionRef.current = pos; }}
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto items-center">
              <div className="w-full sm:w-48">
                <Select value={roleFilter || "all"} onValueChange={handleRoleFilterChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {availableRoles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-full sm:w-48">
                <Select value={statusFilter || "all"} onValueChange={handleStatusFilterChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleCreateUser}
                className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5"
              >
                <Add size={18} />
                Add User
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <Loader message="Loading administrative users..." className="min-h-[300px]" />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center font-semibold">Name</TableHead>
                      <TableHead className="text-center font-semibold">Email</TableHead>
                      <TableHead className="text-center font-semibold">Role</TableHead>
                      <TableHead className="text-center font-semibold">Status</TableHead>
                      <TableHead className="text-center font-semibold">Last Login</TableHead>
                      <TableHead className="text-center font-semibold pr-6">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users?.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users?.map((user) => (
                        <TableRow key={user._id} className="hover:bg-muted/50 transition-colors">
                          <TableCell className="text-center font-medium text-foreground">
                            {user.name}
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">{user.email}</TableCell>
                          <TableCell className="text-center">
                            <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-none font-medium px-2 py-0.5">
                              {availableRoles.find(r => r.value === user.role)?.label || user.role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className={`text-sm font-medium ${
                              user.status === 'ACTIVE' ? 'text-emerald-600' : 'text-muted-foreground'
                            }`}>
                              {user.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">
                            {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                          </TableCell>
                          <TableCell className="text-center pr-6">
                            <TooltipProvider>
                              <div className="flex items-center justify-center gap-1.5">
                                <Tooltip>
                                  <TooltipTrigger className="flex items-center pr-1">
                                    <Switch
                                      checked={user.status === 'ACTIVE'}
                                      onCheckedChange={() => handleStatusToggle(user)}
                                      className="scale-90"
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {user.status === 'ACTIVE' ? 'Deactivate User' : 'Activate User'}
                                  </TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleEditUser(user)}
                                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                    >
                                      <Edit size={16} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit User</TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleResetPassword(user)}
                                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                    >
                                      <Key size={16} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Reset Password</TooltipContent>
                                </Tooltip>

                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDeleteUser(user)}
                                      className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash size={16} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Delete User</TooltipContent>
                                </Tooltip>
                              </div>
                            </TooltipProvider>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Custom Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t gap-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">Rows per page:</span>
                  <Select
                    value={String(rowsPerPage)}
                    onValueChange={(val) => {
                      setRowsPerPage(Number(val));
                      setPage(0);
                    }}
                  >
                    <SelectTrigger className="w-[70px] h-8">
                      <SelectValue placeholder={String(rowsPerPage)} />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25].map((size) => (
                        <SelectItem key={size} value={String(size)}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex flex-col sm:flex-row items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    Showing {pagination.totalItems > 0 ? page * rowsPerPage + 1 : 0}–{Math.min((page + 1) * rowsPerPage, pagination.totalItems)} of {pagination.totalItems}
                  </span>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={(page + 1) * rowsPerPage >= pagination.totalItems}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* User Dialog */}
      <Dialog open={openDialog} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialogType === 'create' && 'Create New User'}
              {dialogType === 'edit' && 'Edit User'}
              {dialogType === 'delete' && 'Delete User'}
              {dialogType === 'resetPassword' && 'Reset Password'}
            </DialogTitle>
            <DialogDescription>
              {dialogType === 'create' && 'Provide account details to onboard a new administrative user.'}
              {dialogType === 'edit' && 'Modify the user details below.'}
              {dialogType === 'delete' && 'Are you sure you want to delete this user?'}
              {dialogType === 'resetPassword' && 'Reset password for this user.'}
            </DialogDescription>
          </DialogHeader>

          {submitError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}
          {submitSuccess && (
            <Alert className="bg-emerald-50 text-emerald-700 border-emerald-200 mb-4">
              <AlertDescription>{submitSuccess}</AlertDescription>
            </Alert>
          )}

          {dialogType === 'delete' ? (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-foreground">
                Are you sure you want to delete the user <span className="font-semibold">"{selectedUser?.name}"</span>?
              </p>
              <p className="text-xs text-muted-foreground">
                This action cannot be undone. The user will lose access to the system.
              </p>
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={handleCloseDialog} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex items-center gap-2"
                >
                  {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  Delete User
                </Button>
              </DialogFooter>
            </div>
          ) : dialogType === 'resetPassword' ? (
            <div className="space-y-4 pt-2">
              <p className="text-sm text-foreground">
                Reset password for <span className="font-semibold">"{selectedUser?.name}"</span>?
              </p>
              <p className="text-xs text-muted-foreground">
                A temporary password will be generated and the user must change it on next login.
              </p>
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={handleCloseDialog} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  onClick={handlePasswordReset}
                  disabled={loading}
                  className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  Reset Password
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <Formik
              initialValues={getInitialFormValues()}
              validationSchema={userValidationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => {
                const hasErrors = Object.keys(errors).length > 0 && Object.keys(touched).length > 0;

                return (
                  <Form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          name="name"
                          placeholder="e.g., John Doe"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={touched.name && errors.name ? "border-destructive focus-visible:ring-destructive" : ""}
                        />
                        {touched.name && errors.name && (
                          <p className="text-xs text-destructive">{errors.name}</p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="email">Email Address</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="e.g., john.doe@optihhcp.com"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={dialogType === 'edit'}
                          className={touched.email && errors.email ? "border-destructive focus-visible:ring-destructive" : ""}
                        />
                        {touched.email && errors.email && (
                          <p className="text-xs text-destructive">{errors.email}</p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="role">Role</Label>
                        <Select
                          value={values.role}
                          onValueChange={(val) => setFieldValue('role', val)}
                        >
                          <SelectTrigger id="role" className={touched.role && errors.role ? "border-destructive focus-visible:ring-destructive" : ""}>
                            <SelectValue placeholder="Select Role" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableRoles.map((role) => (
                              <SelectItem key={role.value} value={role.value}>
                                {role.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {touched.role && errors.role && (
                          <p className="text-xs text-destructive">{errors.role}</p>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="status">Status</Label>
                        <Select
                          value={values.status}
                          onValueChange={(val) => setFieldValue('status', val)}
                        >
                          <SelectTrigger id="status" className={touched.status && errors.status ? "border-destructive focus-visible:ring-destructive" : ""}>
                            <SelectValue placeholder="Select Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ACTIVE">Active</SelectItem>
                            <SelectItem value="INACTIVE">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                        {touched.status && errors.status && (
                          <p className="text-xs text-destructive">{errors.status}</p>
                        )}
                      </div>

                      {dialogType === 'create' && (
                        <div className="md:col-span-2 flex items-center space-x-2 pt-2">
                          <Switch
                            id="sendInvitation"
                            checked={values.sendInvitation}
                            onCheckedChange={(checked) => setFieldValue('sendInvitation', checked)}
                          />
                          <Label htmlFor="sendInvitation" className="cursor-pointer font-normal text-sm">
                            Send invitation email with temporary password
                          </Label>
                        </div>
                      )}
                    </div>

                    <DialogFooter className="mt-6 gap-2 sm:gap-0">
                      <Button type="button" variant="outline" onClick={handleCloseDialog} disabled={isSubmitting}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting || hasErrors}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-2"
                      >
                        {isSubmitting && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        )}
                        {dialogType === 'create' ? 'Create User' : 'Update User'}
                      </Button>
                    </DialogFooter>
                  </Form>
                );
              }}
            </Formik>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;